import { UploadFile } from "@mui/icons-material";
import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Sheet,
  Stack,
  Textarea,
  ToggleButtonGroup,
  Typography,
} from "@mui/joy";
import { format } from "date-fns";
import { HTTPError } from "griptonite-api-client/client";
import {
  ApiCreditProductAdmin,
  ApiCreditProductCodeBatchUpload,
} from "griptonite-api-client/types";
import { useMemo, useState } from "react";

import { useUploadCreditProductCodeBatchMutation } from "@/hooks/credits";
import pluralize from "@/utils/pluralize";

import { parseCodes, readCodeFile } from "./rewardCodes";

const PREVIEW_LENGTH = 5;

type Source = "file" | "paste";

// The file's text once read, or null if it isn't a text file.
type ChosenFile = { file: File; text: string | null };

// The API's errors for an upload come either keyed by field, for a file it can't read, or as a
// single error, for a file whose codes are all already in the pool.
const uploadErrorMessage = (error: Error) => {
  if (error instanceof HTTPError && error.response.status === 400) {
    const detail = error.jsonData?.detail;
    if (detail?.code === "no_new_codes") {
      return "All of these codes have already been added here, so nothing changed.";
    }
    const fieldError = detail?.file?.[0]?.message ?? detail?.venue?.[0]?.message;
    if (fieldError) {
      return fieldError;
    }
  }
  return "Couldn’t add the codes. Please try again.";
};

const CodesPreview = ({ codes, header }: { codes: string[]; header: string | null }) => (
  <Stack gap={0.75}>
    <Typography level="body-sm" fontWeight="lg">
      {pluralize(codes.length, "code")}
      {codes.length > PREVIEW_LENGTH ? `, starting with:` : null}
    </Typography>
    <Sheet
      variant="soft"
      sx={{ borderRadius: "sm", px: 1.5, py: 1, fontFamily: "code", fontSize: "sm" }}
    >
      {header !== null ? (
        <Stack direction="row" gap={1} sx={{ minWidth: 0 }}>
          <Typography
            fontFamily="code"
            level="body-sm"
            textColor="neutral.500"
            noWrap
            sx={{ textDecoration: "line-through" }}
          >
            {header}
          </Typography>
          <Typography level="body-xs" textColor="neutral.500" sx={{ flexShrink: 0 }}>
            header, skipped
          </Typography>
        </Stack>
      ) : null}
      {codes.slice(0, PREVIEW_LENGTH).map((code, index) => (
        <Typography key={index} fontFamily="code" level="body-sm" noWrap>
          {code}
        </Typography>
      ))}
      {codes.length > PREVIEW_LENGTH ? (
        <Typography level="body-sm" textColor="neutral.500">
          and {codes.length - PREVIEW_LENGTH} more
        </Typography>
      ) : null}
    </Sheet>
  </Stack>
);

// Adds a batch of codes to a reward at one gym, from a file or pasted text. Mounted fresh each time
// it opens, so it starts empty.
export default function AddCodesModal({
  reward,
  venue,
  showVenueName,
  onClose,
  onAdded,
}: {
  reward: ApiCreditProductAdmin;
  venue: { uuid: string; name: string };
  showVenueName: boolean;
  onClose: () => void;
  onAdded: (upload: ApiCreditProductCodeBatchUpload) => void;
}) {
  const [source, setSource] = useState<Source>("file");
  const [chosenFile, setChosenFile] = useState<ChosenFile | null>(null);
  const [pasted, setPasted] = useState("");
  const [hasHeader, setHasHeader] = useState(false);
  const mutation = useUploadCreditProductCodeBatchMutation(reward);

  const text = source === "file" ? chosenFile?.text : pasted;
  const parsed = useMemo(() => (text ? parseCodes(text, hasHeader) : null), [text, hasHeader]);
  const isUnreadable = source === "file" && chosenFile !== null && chosenFile.text === null;

  const chooseFile = async (file: File | undefined) => {
    mutation.reset();
    setChosenFile(file ? { file, text: await readCodeFile(file) } : null);
  };

  const submit = () => {
    const upload =
      source === "file"
        ? { file: chosenFile!.file, filename: chosenFile!.file.name }
        : {
            file: new Blob([pasted], { type: "text/plain" }),
            // The API shows a batch by its file's name, so pasted codes need one.
            filename: `Pasted ${format(new Date(), "d MMM yyyy, HH:mm")}`,
          };
    mutation.mutate({ venueUuid: venue.uuid, hasHeader, ...upload }, { onSuccess: onAdded });
  };

  return (
    <Modal open onClose={onClose}>
      <ModalDialog sx={{ width: "100%", maxWidth: 520 }}>
        <ModalClose />
        <DialogTitle>Add codes{showVenueName ? ` at ${venue.name}` : null}</DialogTitle>
        {/* The theme centres dialog text for confirmations; this is a form. */}
        <DialogContent sx={{ textAlign: "left", overflowX: "hidden" }}>
          {/* Joy's checkbox reaches 1px past its box, so it needs room at the bottom. */}
          <Stack gap={2} sx={{ pt: 1, pb: 0.5 }}>
            <Typography level="body-sm">
              One code per line. From a spreadsheet, the codes should be in the first column. Codes
              already added here are skipped.
            </Typography>
            <ToggleButtonGroup
              sx={{ alignSelf: "flex-start" }}
              size="sm"
              value={source}
              onChange={(_, value) => {
                if (value) {
                  mutation.reset();
                  setSource(value);
                }
              }}
            >
              <Button value="file">Upload a file</Button>
              <Button value="paste">Paste codes</Button>
            </ToggleButtonGroup>
            {source === "file" ? (
              <Stack direction="row" alignItems="center" gap={1.5}>
                <Button
                  component="label"
                  variant="outlined"
                  color="neutral"
                  startDecorator={<UploadFile />}
                >
                  {chosenFile ? "Choose another file" : "Choose file"}
                  <input
                    hidden
                    type="file"
                    accept=".txt,.csv,text/plain,text/csv"
                    onChange={(event) => {
                      chooseFile(event.target.files?.[0]);
                      // So choosing the same file again, after editing it, reads it again.
                      event.target.value = "";
                    }}
                  />
                </Button>
                {chosenFile ? (
                  <Typography level="body-sm" noWrap>
                    {chosenFile.file.name}
                  </Typography>
                ) : null}
              </Stack>
            ) : (
              <FormControl>
                <FormLabel>Codes</FormLabel>
                <Textarea
                  minRows={5}
                  maxRows={10}
                  value={pasted}
                  onChange={(event) => {
                    mutation.reset();
                    setPasted(event.target.value);
                  }}
                  sx={{ fontFamily: "code" }}
                />
              </FormControl>
            )}
            <Checkbox
              sx={{ alignSelf: "flex-start" }}
              label="The first row is a header"
              checked={hasHeader}
              onChange={(event) => setHasHeader(event.target.checked)}
            />
            {isUnreadable ? (
              <Alert color="danger">
                This isn’t a text or CSV file. Save a spreadsheet as CSV first.
              </Alert>
            ) : parsed && !parsed.codes.length ? (
              <Alert color="warning">There aren’t any codes in this.</Alert>
            ) : parsed ? (
              <CodesPreview codes={parsed.codes} header={parsed.header} />
            ) : null}
            {mutation.error ? (
              <Alert color="danger">{uploadErrorMessage(mutation.error)}</Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={!parsed?.codes.length} loading={mutation.isPending} onClick={submit}>
            {parsed?.codes.length ? `Add ${pluralize(parsed.codes.length, "code")}` : "Add codes"}
          </Button>
          <Button variant="plain" color="neutral" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </ModalDialog>
    </Modal>
  );
}
